import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { getSections, deleteSection, updateSection, createSection } from '../../services/api';
import { triggerDataRefresh } from '../../context/SiteDataContext';
import ImageUploader from './ImageUploader';
import RichTextEditor from './RichTextEditor';
import './admin.css';

const TestimonialsList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await getSections(true);
      // Sort by order
      const sorted = (response.testimonials || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setTestimonials(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= testimonials.length) return;
    
    setReordering(true);
    try {
      const items = [...testimonials];
      const [movedItem] = items.splice(index, 1);
      items.splice(newIndex, 0, movedItem);
      
      // Update order for both items
      const item1 = items[index];
      const item2 = items[newIndex];
      
      await updateSection(item1.id, 'testimonial', { order: index });
      await updateSection(item2.id, 'testimonial', { order: newIndex });
      
      setTestimonials(items);
      triggerDataRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setReordering(false);
    }
  };

  const handleEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({ ...testimonial });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      await updateSection(editingId, 'testimonial', formData);
      await loadData();
      setEditingId(null);
      setFormData({});
      triggerDataRefresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await deleteSection(id, 'testimonial');
      setTestimonials(testimonials.filter(t => t.id !== id));
      triggerDataRefresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNew = async () => {
    try {
      await createSection('testimonial', {
        name: 'Nouveau lecteur',
        title: 'Lecteur',
        description: 'Avis à compléter...',
        image: 'assets/images/user-2.png',
        link: '',
      });
      await loadData();
      triggerDataRefresh();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <div>
            <h1>Avis / Témoignages</h1>
            <p>Gérez les avis des lecteurs</p>
          </div>
          <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
            + Nouvel avis
          </button>
        </header>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}

        {loading ? (
          <div className="admin-loading"><div className="spinner"></div></div>
        ) : (
          <div className="admin-cards-grid">
            {testimonials.map((item, index) => (
              <div key={item.id} className="admin-card" style={{ position: 'relative' }}>
                {/* Reorder buttons */}
                {!editingId && (
                  <div className="reorder-buttons" style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 10,
                  }}>
                    <button
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0 || reordering}
                      className="admin-btn admin-btn-sm"
                      style={{ padding: '4px 8px', fontSize: '12px', opacity: index === 0 ? 0.3 : 1 }}
                      title="Monter"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveItem(index, 1)}
                      disabled={index === testimonials.length - 1 || reordering}
                      className="admin-btn admin-btn-sm"
                      style={{ padding: '4px 8px', fontSize: '12px', opacity: index === testimonials.length - 1 ? 0.3 : 1 }}
                      title="Descendre"
                    >
                      ↓
                    </button>
                    <span style={{ fontSize: '10px', color: '#999', alignSelf: 'center', marginLeft: '4px' }}>#{index + 1}</span>
                  </div>
                )}
                {editingId === item.id ? (
                  <div className="admin-card-edit">
                    <div className="admin-form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Titre</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Avis</label>
                      <RichTextEditor
                        value={formData.description || ''}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        placeholder="Avis du lecteur..."
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Lien</label>
                      <input
                        type="url"
                        value={formData.link || ''}
                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Photo de l'auteur de l'avis</label>
                      <ImageUploader
                        currentImage={formData.image}
                        onUpload={(url) => setFormData({ ...formData, image: url })}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Image du livre commenté (optionnel)</label>
                      <ImageUploader
                        currentImage={formData.bookImage}
                        onUpload={(url) => setFormData({ ...formData, bookImage: url })}
                      />
                      <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                        Laissez vide si l'avis concerne tous les livres
                      </small>
                    </div>
                    <div className="admin-card-actions">
                      <button onClick={handleCancel} className="admin-btn">Annuler</button>
                      <button onClick={handleSave} className="admin-btn admin-btn-primary">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="admin-card-header">
                      <img src={item.image} alt={item.name} className="admin-avatar" />
                      <div>
                        <h4>{item.name}</h4>
                        <span>{item.title}</span>
                      </div>
                    </div>
                    <p className="admin-card-text">{item.description}</p>
                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(item)} className="admin-btn admin-btn-sm">
                        Modifier
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="admin-btn admin-btn-sm admin-btn-danger">
                        Supprimer
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TestimonialsList;

